import React from 'react';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * StatTile — the standard KPI card.
 *   <StatTile label="Occupancy" value="94%" icon={Home} trend={{value: 3.2, direction: 'up'}} />
 *
 * Deliberately compact: a row of these sits above the actual content on most
 * screens, so it must not eat the first fold. Values scale down on narrow
 * viewports so long currency strings ("KES 130,000.00") stay on one line.
 */
const StatTile = ({
    label,
    value,
    hint,
    icon: Icon,
    trend,           // { value: number, direction: 'up'|'down', suffix?: string }
    accent,          // 'primary' | 'success' | 'warning' | 'danger' | undefined
    loading = false,
}) => {
    const accentStyle = {
        primary: 'bg-gradient-to-br from-primary-50 to-primary-100/60 text-primary-700 ring-primary-700/10',
        success: 'bg-gradient-to-br from-success-50 to-success-100/60 text-success-700 ring-success-700/10',
        warning: 'bg-gradient-to-br from-warning-50 to-warning-100/60 text-warning-700 ring-warning-700/10',
        danger:  'bg-gradient-to-br from-danger-50 to-danger-100/60 text-danger-700 ring-danger-700/10',
    }[accent] || 'bg-gradient-to-br from-neutral-50 to-neutral-100 text-neutral-600 ring-neutral-900/5';

    return (
        <div className="card card-hover p-4 sm:p-5 animate-fade-in relative overflow-hidden group">
            {/* Subtle top specular accent highlight */}
            <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-neutral-200/50 group-hover:via-primary-500/30 to-transparent transition-colors" />

            <div className="flex items-start justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase leading-tight tracking-wider text-neutral-500">
                    {label}
                </div>
                {Icon && (
                    <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-xl ring-1 shadow-xs transition-transform duration-200 group-hover:scale-105 ${accentStyle}`}>
                        <Icon className="h-4 w-4" />
                    </div>
                )}
            </div>

            <div className="mt-2.5">
                {loading ? (
                    <div className="h-7 w-24 animate-pulse-soft rounded-md bg-neutral-100" />
                ) : (
                    <div className="kpi-value text-xl font-bold tracking-tight tabular-nums text-neutral-900 sm:text-2xl lg:text-3xl">
                        {value}
                    </div>
                )}
            </div>

            {(trend || hint) && (
                <div className="mt-2.5 flex items-center justify-between gap-2 pt-2 border-t border-neutral-100/80">
                    {hint && <div className="text-xs text-neutral-500 truncate">{hint}</div>}
                    {trend && (
                        <div className={`inline-flex shrink-0 items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ring-1
                            ${trend.direction === 'up'
                                ? 'bg-success-50 text-success-700 ring-success-600/15'
                                : 'bg-danger-50 text-danger-700 ring-danger-600/15'}`}>
                            {trend.direction === 'up' ? <ArrowUpRight className="h-3 w-3 stroke-[2.5]" /> : <ArrowDownRight className="h-3 w-3 stroke-[2.5]" />}
                            <span>{trend.value}{trend.suffix || '%'}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default StatTile;
